import React, { useEffect, useState } from 'react';
import api from '../../utils/api';

const emptyVideo = () => ({ url: '', title: '' });
const emptyWeek = (week) => ({ week, title: '', released: false, videos: [emptyVideo()] });

const DAY_LABELS = {
  monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday',
  thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday'
};

const MaterialsManager = ({ courseId, courseName, onBack }) => {
  const [materials, setMaterials] = useState([]);
  const [currentWeek, setCurrentWeek] = useState(1);
  const [autoRelease, setAutoRelease] = useState(true);
  const [releaseDay, setReleaseDay] = useState('');
  const [releaseDelay, setReleaseDelay] = useState('24_hours');
  const [scheduleDays, setScheduleDays] = useState([]);
  const [scheduleTime, setScheduleTime] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [confirmDialog, setConfirmDialog] = useState(null); // { type, data }

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await api.get(`/materials/${courseId}`);
        setMaterials(data.materials.length > 0 ? data.materials : [emptyWeek(1)]);
        setCurrentWeek(data.currentWeek || 1);
        setAutoRelease(data.autoReleaseMaterials !== false);
        setReleaseDay(data.materialReleaseDay || data.scheduleDays?.[0] || '');
        setReleaseDelay(data.materialReleaseDelay || '24_hours');
        setScheduleDays(data.scheduleDays || []);
        setScheduleTime(data.scheduleTime || '');
      } catch (err) {
        setError('Failed to load materials');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [courseId]);

  const addWeek = () => {
    const nextWeek = materials.length > 0 ? Math.max(...materials.map(m => m.week)) + 1 : 1;
    setMaterials(prev => [...prev, emptyWeek(nextWeek)]);
    setSaved(false);
  };

  const removeWeek = (index) => {
    setMaterials(prev => prev.filter((_, i) => i !== index));
    setSaved(false);
  };

  const updateWeek = (index, field, value) => {
    setMaterials(prev => prev.map((m, i) => i === index ? { ...m, [field]: value } : m));
    setSaved(false);
  };

  const toggleRelease = (index) => {
    setMaterials(prev => prev.map((m, i) => i === index ? { ...m, released: !m.released } : m));
    setSaved(false);
  };

  const addVideo = (weekIndex) => {
    setMaterials(prev => prev.map((m, i) =>
      i === weekIndex ? { ...m, videos: [...m.videos, emptyVideo()] } : m
    ));
    setSaved(false);
  };

  const updateVideo = (weekIndex, videoIndex, field, value) => {
    setMaterials(prev => prev.map((m, wi) =>
      wi === weekIndex
        ? { ...m, videos: m.videos.map((v, vi) => vi === videoIndex ? { ...v, [field]: value } : v) }
        : m
    ));
    setSaved(false);
  };

  const removeVideo = (weekIndex, videoIndex) => {
    setMaterials(prev => prev.map((m, wi) =>
      wi === weekIndex
        ? { ...m, videos: m.videos.filter((_, vi) => vi !== videoIndex) }
        : m
    ));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const cleaned = materials
        .filter(m => m.videos.some(v => v.url.trim()))
        .map(m => ({
          week: m.week,
          title: m.title,
          released: m.released || false,
          videos: m.videos.filter(v => v.url.trim())
        }));

      await api.put(`/materials/${courseId}`, {
        materials: cleaned,
        currentWeek,
        autoReleaseMaterials: autoRelease,
        materialReleaseDay: releaseDay,
        materialReleaseDelay: releaseDelay
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save materials');
    } finally {
      setSaving(false);
    }
  };

  const handleAdvanceWeek = () => {
    const newWeek = currentWeek + 1;
    const weekMaterial = materials.find(m => m.week === newWeek);
    setConfirmDialog({
      type: 'advance',
      message: `Advance to Week ${newWeek}?`,
      detail: weekMaterial
        ? `"${weekMaterial.title || `Week ${newWeek}`}" will ${autoRelease ? 'be auto-released to students' : 'need to be manually released'}.`
        : `No materials added for Week ${newWeek} yet.`,
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          const { data } = await api.patch(`/materials/${courseId}/advance`);
          setCurrentWeek(data.currentWeek);
          const refreshed = await api.get(`/materials/${courseId}`);
          setMaterials(refreshed.data.materials);
          setSaved(false);
        } catch (err) {
          setError(err.response?.data?.error || 'Failed to advance week');
        }
      }
    });
  };

  const handleSetWeek = (week) => {
    setConfirmDialog({
      type: 'set-week',
      message: `Set current week to ${week}?`,
      detail: `Students will see released materials up to Week ${week}.`,
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          await api.patch(`/materials/${courseId}/set-week`, { week });
          setCurrentWeek(week);
          setSaved(false);
        } catch (err) {
          setError(err.response?.data?.error || 'Failed to set week');
        }
      }
    });
  };

  if (loading) return <div className="text-gray-500">Loading materials...</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <button onClick={onBack} className="text-sm text-gray-500 hover:text-gray-700 mb-1">&larr; Back to Materials</button>
          <h1 className="text-2xl font-bold text-gray-900">Materials: {courseName}</h1>
        </div>
      </div>

      {/* Current Week + Schedule + Release Settings */}
      <div className="bg-white p-4 sm:p-5 rounded-xl border border-gray-100 shadow-sm mb-6 space-y-4">
        {/* Row 1: Current week + Advance */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-sm text-gray-500">Current Week</p>
            <div className="flex items-center gap-2 mt-1">
              <input
                type="number"
                min={1}
                value={currentWeek}
                onChange={e => { setCurrentWeek(parseInt(e.target.value) || 1); setSaved(false); }}
                className="w-16 text-2xl font-bold text-brand-500 border border-gray-200 rounded-lg px-2 py-0.5 text-center focus:ring-2 focus:ring-brand-400 focus:border-transparent"
              />
              <button
                onClick={handleAdvanceWeek}
                className="text-xs bg-brand-50 text-brand-600 px-3 py-1.5 rounded-lg font-medium hover:bg-brand-100 transition-colors"
              >
                Advance to Week {currentWeek + 1} →
              </button>
            </div>
          </div>

          {/* Class schedule info */}
          {scheduleDays.length > 0 && (
            <div className="text-right">
              <p className="text-xs text-gray-400">Class Schedule</p>
              <p className="text-sm font-medium text-gray-700">
                {scheduleDays.map(d => DAY_LABELS[d] || d).join(' & ')}
                {scheduleTime && ` at ${scheduleTime}`}
              </p>
            </div>
          )}
        </div>

        {/* Row 2: Auto-release + Release day + Delay */}
        <div className="border-t border-gray-100 pt-4 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => { setAutoRelease(!autoRelease); setSaved(false); }}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                autoRelease ? 'bg-brand-500' : 'bg-gray-200'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                autoRelease ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
            <div>
              <p className="text-sm font-medium text-gray-700">Auto-release materials</p>
              <p className="text-xs text-gray-400">Automatically release after class</p>
            </div>
          </div>

          {autoRelease && scheduleDays.length > 1 && (
            <div>
              <label className="block text-xs text-gray-400 mb-1">Release after</label>
              <select
                value={releaseDay}
                onChange={e => { setReleaseDay(e.target.value); setSaved(false); }}
                className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-brand-400 focus:border-transparent"
              >
                {scheduleDays.map(d => (
                  <option key={d} value={d}>{DAY_LABELS[d] || d} class</option>
                ))}
              </select>
            </div>
          )}

          {autoRelease && (
            <div>
              <label className="block text-xs text-gray-400 mb-1">Send timing</label>
              <select
                value={releaseDelay}
                onChange={e => { setReleaseDelay(e.target.value); setSaved(false); }}
                className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-brand-400 focus:border-transparent"
              >
                <option value="immediately">Immediately after class</option>
                <option value="1_hour">1 hour after class</option>
                <option value="24_hours">24 hours after class</option>
              </select>
            </div>
          )}
        </div>
      </div>

      <p className="text-sm text-gray-500 mb-4">
        Add YouTube links per week. Students see materials for weeks up to the current week that are marked as released.
        {autoRelease && releaseDay && ` Materials auto-release after ${DAY_LABELS[releaseDay] || releaseDay} class${releaseDelay === '24_hours' ? ' (24h delay)' : ''}.`}
      </p>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      <div className="space-y-4">
        {materials.map((week, wi) => {
          const isCurrent = week.week === currentWeek;
          const isPast = week.week < currentWeek;

          return (
            <div key={wi} className={`bg-white p-5 rounded-xl border shadow-sm ${
              isCurrent ? 'border-brand-300 ring-1 ring-brand-200' : 'border-gray-100'
            }`}>
              <div className="mb-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => week.week !== currentWeek && handleSetWeek(week.week)}
                      title={week.week !== currentWeek ? `Set as current week` : 'Current week'}
                      className={`px-2.5 py-1 rounded-lg text-sm font-bold transition-colors ${
                        isCurrent ? 'bg-brand-500 text-white' :
                        isPast ? 'bg-green-100 text-green-700 hover:bg-green-200 cursor-pointer' :
                        'bg-gray-100 text-gray-500 hover:bg-gray-200 cursor-pointer'
                      }`}
                    >
                      W{week.week}
                    </button>
                    {isCurrent && <span className="text-xs text-brand-500 font-medium">Current</span>}
                    {isPast && <span className="text-xs text-green-500 font-medium">Done</span>}
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-medium ${week.released ? 'text-green-600' : 'text-gray-400'}`}>
                        {week.released ? 'Released' : 'Held'}
                      </span>
                      <button
                        onClick={() => toggleRelease(wi)}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          week.released ? 'bg-green-500' : 'bg-gray-200'
                        }`}
                      >
                        <span className="inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform" style={{ transform: week.released ? 'translateX(18px)' : 'translateX(2px)' }} />
                      </button>
                    </div>
                    {materials.length > 1 && (
                      <button onClick={() => removeWeek(wi)} className="text-gray-400 hover:text-red-500 text-sm">
                        Remove
                      </button>
                    )}
                  </div>
                </div>
                <input
                  type="text"
                  value={week.title}
                  onChange={e => updateWeek(wi, 'title', e.target.value)}
                  placeholder="Week title (optional)"
                  className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                />
              </div>

              <div className="space-y-3">
                {week.videos.map((video, vi) => (
                  <div key={vi} className="flex items-start gap-2">
                    <span className="text-xs text-gray-400 w-4 mt-2">{vi + 1}.</span>
                    <div className="flex-1 space-y-1.5">
                      <input
                        type="url"
                        value={video.url}
                        onChange={e => updateVideo(wi, vi, 'url', e.target.value)}
                        placeholder="https://youtube.com/watch?v=..."
                        className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                      />
                      <input
                        type="text"
                        value={video.title}
                        onChange={e => updateVideo(wi, vi, 'title', e.target.value)}
                        placeholder="Video title"
                        className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                      />
                    </div>
                    {week.videos.length > 1 && (
                      <button onClick={() => removeVideo(wi, vi)} className="text-gray-400 hover:text-red-500 mt-2">&times;</button>
                    )}
                  </div>
                ))}
                <button onClick={() => addVideo(wi)} className="text-xs text-brand-500 hover:text-brand-600 font-medium ml-6">
                  + Add Video
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <button onClick={addWeek} className="mt-4 text-sm text-brand-500 hover:text-brand-600 font-medium">
        + Add Week
      </button>

      <div className="mt-6 flex items-center justify-end gap-3">
        {saved && <span className="text-sm text-green-600 font-medium">Saved</span>}
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-brand-500 text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-brand-600 disabled:bg-gray-300 transition-colors"
        >
          {saving ? 'Saving...' : 'Save Materials'}
        </button>
      </div>

      {/* Confirmation Dialog */}
      {confirmDialog && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setConfirmDialog(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{confirmDialog.message}</h3>
            <p className="text-sm text-gray-500 mb-6">{confirmDialog.detail}</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmDialog(null)}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDialog.onConfirm}
                className="px-4 py-2 text-sm bg-brand-500 text-white rounded-lg font-medium hover:bg-brand-600 transition-colors"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaterialsManager;
